"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useId, useState, type ReactNode } from "react";
import { ShoppingBag } from "lucide-react";

import { analytics } from "@/lib/analytics";
import { buildMenuCtaTrackingProperties } from "@/lib/menu-cta-tracking";
import { externalLinkProps } from "@/lib/external-link-props";
import {
  getRomaLocationOrderDestination,
  resolveRomaGenericOrderBehavior,
  type RomaOrderDestination,
} from "@/lib/roma-order-destinations";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const MOBILE_CHOOSER_BREAKPOINT = "(max-width: 767px)";

/** Solid funnel surface. `bg-popover` is white/`oklch(1 0 0)` on public pages and
 *  can lose to hero stacking; force an opaque fill and sit above hero `z-10`. */
export const ROMA_ORDER_CHOOSER_SURFACE_CLASS =
  "z-[80] isolate overflow-hidden border-white/12 !bg-[#161616] text-white shadow-lg [background-color:#161616]";

type RomaOrderChooserProps = {
  trackingLocation: string;
  triggerLabel?: string;
  className?: string;
  children?: ReactNode;
  pathname?: string;
  onDestinationSelect?: () => void;
};

function usePrefersMobileChooser() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_CHOOSER_BREAKPOINT);
    const update = () => {
      setIsMobile(media.matches);
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isMobile;
}

export function trackRomaOrderDestination(
  destination: RomaOrderDestination,
  trackingLocation: string,
  pathname: string,
  currentUrl?: string,
) {
  const tracking = buildMenuCtaTrackingProperties({
    location: trackingLocation,
    label: destination.locationSlug,
    ctaText: destination.ctaLabel,
    destinationUrl: destination.href,
    currentUrl: currentUrl ?? destination.href,
    locationSlug: destination.locationSlug,
    locationId: destination.locationId,
    orderProvider: destination.provider,
  });

  analytics.orderStarted({
    ...tracking,
    page_path: pathname,
    order_target: destination.provider,
  });
  analytics.ctaClicked(destination.ctaLabel, trackingLocation, tracking);
}

function RomaOrderDestinationLink({
  destination,
  trackingLocation,
  pathname,
  className,
  onNavigate,
  role,
}: {
  destination: RomaOrderDestination;
  trackingLocation: string;
  pathname: string;
  className?: string;
  onNavigate?: () => void;
  role?: "menuitem";
}) {
  const handleClick = () => {
    trackRomaOrderDestination(
      destination,
      trackingLocation,
      pathname,
      typeof window !== "undefined" ? window.location.href : destination.href,
    );
    onNavigate?.();
  };

  if (destination.opensInNewTab) {
    return (
      <a
        href={destination.href}
        {...externalLinkProps(destination.href)}
        className={className}
        role={role}
        onClick={handleClick}
      >
        <span className="block font-semibold">{destination.cityLabel}</span>
        <span className="block text-sm font-normal opacity-80">
          {destination.ctaLabel}
        </span>
      </a>
    );
  }

  return (
    <Link
      href={destination.href}
      prefetch={false}
      className={className}
      role={role}
      onClick={handleClick}
    >
      <span className="block font-semibold">{destination.cityLabel}</span>
      <span className="block text-sm font-normal opacity-80">
        {destination.ctaLabel}
      </span>
    </Link>
  );
}

function optionClassName() {
  return "flex min-h-12 flex-col justify-center rounded-lg px-3 py-2 text-left text-white transition-colors hover:bg-white/8 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ef6a5f]";
}

export function RomaLocationOrderLink({
  slug,
  trackingLocation,
  className,
  children,
}: {
  slug: RomaOrderDestination["locationSlug"];
  trackingLocation: string;
  className?: string;
  children?: ReactNode;
}) {
  const pathname = usePathname() ?? "/";
  const destination = getRomaLocationOrderDestination(slug);
  const handleClick = () => {
    trackRomaOrderDestination(
      destination,
      trackingLocation,
      pathname,
      typeof window !== "undefined" ? window.location.href : destination.href,
    );
  };

  if (destination.opensInNewTab) {
    return (
      <a
        href={destination.href}
        {...externalLinkProps(destination.href)}
        className={className}
        data-track="order_started"
        data-track-location={trackingLocation}
        data-track-label={destination.locationSlug}
        data-track-location-slug={destination.locationSlug}
        data-track-location-id={destination.locationId}
        data-track-provider={destination.provider}
        onClick={handleClick}
      >
        {children ?? destination.ctaLabel}
      </a>
    );
  }

  return (
    <Link
      href={destination.href}
      prefetch={false}
      className={className}
      data-track="order_started"
      data-track-location={trackingLocation}
      data-track-label={destination.locationSlug}
      data-track-location-slug={destination.locationSlug}
      data-track-location-id={destination.locationId}
      data-track-provider={destination.provider}
      onClick={handleClick}
    >
      {children ?? destination.ctaLabel}
    </Link>
  );
}

export function RomaOrderCta({
  trackingLocation,
  triggerLabel = "Order",
  className,
  children,
  pathname: pathnameProp,
  onDestinationSelect,
}: RomaOrderChooserProps) {
  const routePathname = usePathname() ?? "/";
  const pathname = pathnameProp ?? routePathname;
  const behavior = resolveRomaGenericOrderBehavior(pathname);
  const titleId = useId();
  const isMobile = usePrefersMobileChooser();
  const [open, setOpen] = useState(false);

  if (behavior.kind === "direct") {
    const destination = behavior.destination;
    const directLabel =
      destination.provider === "grubhub"
        ? destination.ctaLabel
        : (children ?? destination.ctaLabel ?? triggerLabel);
    const handleClick = () => {
      trackRomaOrderDestination(
        destination,
        trackingLocation,
        pathname,
        typeof window !== "undefined" ? window.location.href : destination.href,
      );
      onDestinationSelect?.();
    };

    if (destination.opensInNewTab) {
      return (
        <a
          href={destination.href}
          {...externalLinkProps(destination.href)}
          className={className}
          onClick={handleClick}
        >
          {directLabel}
        </a>
      );
    }

    return (
      <Link
        href={destination.href}
        prefetch={false}
        className={className}
        onClick={handleClick}
      >
        {directLabel}
      </Link>
    );
  }

  const trigger = (
    <button type="button" className={className} aria-haspopup="dialog">
      {children ?? (
        <>
          <ShoppingBag className="mr-1.5 h-4 w-4" aria-hidden="true" />
          {triggerLabel}
        </>
      )}
    </button>
  );

  const options = (itemRole?: "menuitem") =>
    behavior.options.map((destination) => (
      <RomaOrderDestinationLink
        key={destination.locationId}
        destination={destination}
        trackingLocation={trackingLocation}
        pathname={pathname}
        className={optionClassName()}
        role={itemRole}
        onNavigate={() => {
          setOpen(false);
          onDestinationSelect?.();
        }}
      />
    ));

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="bottom"
          className={ROMA_ORDER_CHOOSER_SURFACE_CLASS}
        >
          <SheetHeader>
            <SheetTitle id={titleId} className="text-white">
              Choose a location
            </SheetTitle>
            <SheetDescription className="text-white/70">
              Both locations order on Grubhub.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-2 px-4 pb-6">
            {options()}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className={`w-72 p-2 ${ROMA_ORDER_CHOOSER_SURFACE_CLASS}`}
        aria-labelledby={titleId}
      >
        <p id={titleId} className="px-3 pb-2 text-sm font-semibold text-white">
          Choose a location
        </p>
        <div role="menu">{options("menuitem")}</div>
      </PopoverContent>
    </Popover>
  );
}
